import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import { api, ApiError } from '../../lib/api';
import DateInput from '../ui/DateInput';
import {
  MEDIA_PACKAGE_MAX_EVENTS,
  MEDIA_PACKAGE_QUICK_PICKS,
  clampMediaPackageEvents,
  mediaPackageDescription,
  mediaPackageListPriceDollars,
} from '../../lib/media';

// NewProjectDialog — replaces the prior window.prompt('Project name:') in
// AccountDetail's Projects tab (item 1.8). Shipped with the rich-form fields
// the brief calls out: name + type + target_delivery_date + contract_value,
// plus an initial deliverables checkbox set pre-populated by project type.
//
// Submits via POST /api/admin/projects which auto-seeds the 4 default
// deliverables for type='website'. The dialog accepts a project_type radio
// for future-proofing the audit_only / maintenance_uplift / custom variants
// even though the schema CHECK still allows only the canonical 4 types.

const PROJECT_TYPES = [
  { value: 'website',         label: 'Website build', defaultContractDollars: 4999 },
  { value: 'redesign',        label: 'Redesign',      defaultContractDollars: 4999 },
  // Maintenance is fixed-price ($3,500 / 1 Year Ongoing Maintenance) and
  // skips the build pipeline entirely — starts at status='maintained'.
  { value: 'maintenance',     label: 'Maintenance',   defaultContractDollars: 3500, fixedPrice: true },
  // Media package is the other service line: tier-priced (Standard $7,500 /
  // Premium $9,500), no build pipeline, and the auto-drafted invoice's signed
  // line item is what activates the school's booking calendar.
  { value: 'media_package',   label: 'Media package', defaultContractDollars: 7500, fixedPrice: true },
  { value: 'brillabee_pilot', label: 'Brillabee pilot', defaultContractDollars: 0 },
  { value: 'other',           label: 'Custom / other', defaultContractDollars: 0 },
];

// Media packages: one product, variable event count. Quick picks are the
// published price points; any other count defaults to the per-event rate and
// the price stays editable ($0 = a complimentary grant).
const MEDIA_DEFAULT_EVENTS = MEDIA_PACKAGE_QUICK_PICKS[0];

const DEFAULT_DELIVERABLES_BY_TYPE = {
  website: ['site_build', 'media_capture', 'accessibility_audit', 'domain_cutover'],
  redesign: ['site_build', 'accessibility_audit'],
  maintenance: [], // no deliverables — maintenance is a renewing service line
  media_package: [], // service line — shoots are booked via the media calendar, not deliverables
  brillabee_pilot: ['site_build'],
  other: [],
};

const ALL_DELIVERABLE_TYPES = ['site_build', 'media_capture', 'accessibility_audit', 'domain_cutover'];

const inputCls = 'w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder-gray-600';
const labelCls = 'block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5';

function addDays(today, n) {
  const d = new Date(`${today}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// Default project name — Maintenance gets the year suffix so a customer
// who's on year 3 can have "Maintenance 2026 — P.S. 11X" alongside next
// year's "Maintenance 2027 — P.S. 11X" without a slug collision.
function defaultNameForType(type, account, mediaEvents = MEDIA_DEFAULT_EVENTS) {
  if (!account) return '';
  if (type === 'maintenance') {
    const year = new Date().getUTCFullYear();
    return `Maintenance ${year} — ${account.name}`;
  }
  if (type === 'media_package') {
    const n = clampMediaPackageEvents(mediaEvents);
    return `Media Package (${n} ${n === 1 ? 'event' : 'events'}) — ${account.name}`;
  }
  return `${account.name} Website`;
}

export default function NewProjectDialog({ open, account, onClose, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const [name, setName] = useState('');
  const [type, setType] = useState('website');
  const [targetDate, setTargetDate] = useState('');
  const [contractValue, setContractValue] = useState(4999);
  const [deliverables, setDeliverables] = useState(DEFAULT_DELIVERABLES_BY_TYPE.website);
  // Maintenance-only: opt-in checkbox, default true, controls whether the
  // backend auto-creates the $3,500 draft invoice on project creation.
  const [createMaintenanceInvoice, setCreateMaintenanceInvoice] = useState(true);
  // Media-package-only: tier + the matching auto-draft-invoice checkbox.
  const [mediaEvents, setMediaEvents] = useState(MEDIA_DEFAULT_EVENTS);
  const [mediaDollars, setMediaDollars] = useState(mediaPackageListPriceDollars(MEDIA_DEFAULT_EVENTS));
  const [createMediaInvoice, setCreateMediaInvoice] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState({});

  useEffect(() => {
    if (!open) return;
    setName(defaultNameForType('website', account));
    setType('website');
    setTargetDate(addDays(today, 30));
    setContractValue(4999);
    setDeliverables(DEFAULT_DELIVERABLES_BY_TYPE.website);
    setCreateMaintenanceInvoice(true);
    setMediaEvents(MEDIA_DEFAULT_EVENTS);
    setMediaDollars(mediaPackageListPriceDollars(MEDIA_DEFAULT_EVENTS));
    setCreateMediaInvoice(true);
    setError(''); setFieldError({});
    // `today` is computed on every render but is only meaningful here on
    // open; intentionally omitted from deps to avoid re-firing on every
    // millisecond.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, account]);

  // When project type changes, repopulate the deliverable defaults + the
  // contract-value default + the auto-name. The user can then override.
  const onTypeChange = (next) => {
    setType(next);
    const def = PROJECT_TYPES.find((t) => t.value === next);
    if (def) setContractValue(def.defaultContractDollars);
    setDeliverables(DEFAULT_DELIVERABLES_BY_TYPE[next] || []);
    setName(defaultNameForType(next, account, mediaEvents));
  };

  // Changing the count re-fills list price (admin can then override) and
  // refreshes the default project name.
  const onMediaEventsChange = (value) => {
    setMediaEvents(value);
    const n = clampMediaPackageEvents(value);
    const dollars = mediaPackageListPriceDollars(n);
    setMediaDollars(dollars);
    setContractValue(dollars);
    setName(defaultNameForType('media_package', account, n));
  };
  const onMediaDollarsChange = (value) => {
    setMediaDollars(value);
    setContractValue(value);
  };

  const isMaintenance = type === 'maintenance';
  const isMediaPackage = type === 'media_package';
  const mediaEventCount = clampMediaPackageEvents(mediaEvents);
  const mediaPriceDollars = Math.max(0, Number(mediaDollars) || 0);

  const toggleDeliverable = (t) => {
    setDeliverables((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setFieldError({});
    if (!name.trim()) { setFieldError({ name: 'Name is required' }); return; }
    if (!account?.id) { setError('Missing account context'); return; }
    setSubmitting(true);
    try {
      // First: create the project. The backend auto-seeds the 4 default
      // deliverables when type='website' AND seed_defaults isn't false. Sprint
      // C delegates the deliverable-list customization to the backend's
      // existing seed_defaults flag — when the user un-ticks defaults we set
      // seed_defaults=false and explicitly POST the chosen deliverables after.
      const slug = `${account.slug || 'project'}-${Date.now().toString(36).slice(-6)}`;
      const useDefaults = type === 'website' &&
        deliverables.length === DEFAULT_DELIVERABLES_BY_TYPE.website.length &&
        DEFAULT_DELIVERABLES_BY_TYPE.website.every((d) => deliverables.includes(d));
      const isServiceLine = isMaintenance || isMediaPackage;
      const payload = {
        accountId: account.id,
        name: name.trim(),
        slug,
        type,
        targetDeliveryDate: isServiceLine ? null : (targetDate || null),
        // contractValue is ignored server-side for the fixed-price service
        // lines (maintenance $3,500; media package by tier), but we still send
        // the dollars→cents conversion for the other types. Skip the field
        // entirely on service lines to keep the request shape obvious.
        ...(isServiceLine ? {} : {
          contractValue: Number.isFinite(Number(contractValue)) ? Math.round(Number(contractValue) * 100) : null,
        }),
        seed_defaults: useDefaults,
        ...(isMaintenance ? { create_maintenance_invoice: createMaintenanceInvoice } : {}),
        ...(isMediaPackage ? {
          media_package_events: mediaEventCount,
          media_package_price_cents: Math.round(mediaPriceDollars * 100),
          create_media_invoice: createMediaInvoice,
        } : {}),
      };
      const created = await api.post('/api/admin/projects', payload);
      const projectId = created?.project?.id;
      // If we skipped seed_defaults, post the chosen list manually.
      // Maintenance never gets manual deliverables (the type is service-only).
      if (!isMaintenance && !useDefaults && projectId && deliverables.length > 0) {
        for (const t of deliverables) {
          await api.post(`/api/admin/projects/${projectId}/deliverables`, { type: t });
        }
      }
      onCreated?.(created.project, { draftInvoiceId: created?.draftInvoiceId });
      onClose?.();
    } catch (err) {
      if (err instanceof ApiError && err.data?.fieldErrors) setFieldError(err.data.fieldErrors);
      setError(err?.data?.error || err?.message || 'Failed to create project');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title="New project"
      maxWidth="max-w-xl"
      footer={
        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} disabled={submitting}
            className="px-4 py-2 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white">
            Cancel
          </button>
          <button type="submit" form="new-project-form" disabled={submitting || !name.trim()}
            data-testid="new-project-submit"
            className="px-5 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] disabled:opacity-50">
            {submitting ? 'Creating…' : 'Create project'}
          </button>
        </div>
      }
    >
      <form id="new-project-form" onSubmit={submit} className="space-y-3">
        {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-2">{error}</div>}
        <label className="block">
          <span className={labelCls}>Project name <span className="text-red-400/80">*</span></span>
          <input data-testid="new-project-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          {fieldError.name && <p className="mt-1 text-[11px] text-red-400">{fieldError.name}</p>}
        </label>
        <div>
          <span className={labelCls}>Project type</span>
          <div className="flex flex-wrap gap-2" data-testid="new-project-type">
            {PROJECT_TYPES.map((t) => (
              <label key={t.value} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] border cursor-pointer ${type === t.value ? 'bg-[#B0FC38]/15 border-[#B0FC38]/30 text-white' : 'bg-white/[0.02] border-white/[0.08] text-gray-300'}`}>
                <input type="radio" name="project-type" className="sr-only" checked={type === t.value} onChange={() => onTypeChange(t.value)} />
                {t.label}
              </label>
            ))}
          </div>
        </div>
        {isMediaPackage ? (
          <div data-testid="new-project-media-summary" className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3 space-y-3">
            <div>
              <span className={labelCls}>Package size</span>
              <div className="flex flex-wrap items-center gap-2" data-testid="new-project-media-size">
                {MEDIA_PACKAGE_QUICK_PICKS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`new-project-media-pick-${n}`}
                    onClick={() => onMediaEventsChange(n)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] border ${mediaEventCount === n && mediaPriceDollars === mediaPackageListPriceDollars(n) ? 'bg-[#B0FC38]/15 border-[#B0FC38]/30 text-white' : 'bg-white/[0.02] border-white/[0.08] text-gray-300'}`}
                  >
                    {n} events · ${mediaPackageListPriceDollars(n).toLocaleString()}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className={labelCls}>Events (1–{MEDIA_PACKAGE_MAX_EVENTS})</span>
                <input
                  type="number" min={1} max={MEDIA_PACKAGE_MAX_EVENTS} step={1}
                  value={mediaEvents}
                  onChange={(e) => onMediaEventsChange(e.target.value)}
                  className={inputCls}
                  data-testid="new-project-media-events"
                />
              </label>
              <label className="block">
                <span className={labelCls}>Price (USD · 0 = complimentary)</span>
                <input
                  type="number" min={0} step={1}
                  value={mediaDollars}
                  onChange={(e) => onMediaDollarsChange(e.target.value)}
                  className={inputCls}
                  data-testid="new-project-media-price-input"
                />
              </label>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[12px] text-gray-400">Package price</span>
              <span className="text-[14px] font-semibold text-white" data-testid="new-project-media-price">
                {mediaPriceDollars === 0 ? 'Complimentary · $0.00' : `$${mediaPriceDollars.toLocaleString()}.00`}
              </span>
            </div>
            <div className="text-[11px] text-gray-500 leading-relaxed">
              Single line item: <strong>{mediaPackageDescription(mediaEventCount)}</strong>. Tax exempt, signature required. When the school signs the invoice in their portal, the package activates automatically — booking calendar, {mediaEventCount} shoot {mediaEventCount === 1 ? 'credit' : 'credits'}, and delivery tracking. List price fills in from the event count; override it for a quoted or complimentary deal. Manage the package from the account's Media tab after signing.
            </div>
            <label className="mt-2 flex items-start gap-2 text-[12px] text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={createMediaInvoice}
                onChange={(e) => setCreateMediaInvoice(e.target.checked)}
                className="accent-[#B0FC38] mt-0.5"
                data-testid="new-project-create-media-invoice"
              />
              <span>
                Auto-create draft invoice
                <span className="block text-[10px] text-gray-500 mt-0.5">Uncheck if you'd rather add the media-package line item to an invoice manually from the Financial page.</span>
              </span>
            </label>
          </div>
        ) : isMaintenance ? (
          <div data-testid="new-project-maintenance-summary" className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3 space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[12px] text-gray-400">Annual fee</span>
              <span className="text-[14px] font-semibold text-white">$3,500.00 / year</span>
            </div>
            <div className="text-[11px] text-gray-500 leading-relaxed">
              Single line item: <strong>1 Year Ongoing Maintenance</strong>. Tax exempt. Project starts at status <em>maintained</em> with no deliverables. Renewal next year is tracked from the Maintenance renewals widget on /admin/financial.
            </div>
            <label className="mt-2 flex items-start gap-2 text-[12px] text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={createMaintenanceInvoice}
                onChange={(e) => setCreateMaintenanceInvoice(e.target.checked)}
                className="accent-[#B0FC38] mt-0.5"
                data-testid="new-project-create-maintenance-invoice"
              />
              <span>
                Auto-create draft invoice
                <span className="block text-[10px] text-gray-500 mt-0.5">Uncheck if you'd rather create the invoice manually from the Financial page after.</span>
              </span>
            </label>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className={labelCls}>Target delivery</span>
                <DateInput data-testid="new-project-target-date" value={targetDate} onChange={setTargetDate} className={inputCls} />
              </label>
              <label className="block">
                <span className={labelCls}>Contract value (USD)</span>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    data-testid="new-project-contract-value"
                    value={contractValue}
                    onChange={(e) => setContractValue(e.target.value)}
                    className={`${inputCls} pl-6`}
                  />
                </div>
              </label>
            </div>
            <div>
              <span className={labelCls}>Initial deliverables</span>
              <div className="flex flex-wrap gap-2" data-testid="new-project-deliverables">
                {ALL_DELIVERABLE_TYPES.map((t) => (
                  <label key={t} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] border bg-white/[0.02] border-white/[0.08] text-gray-300 cursor-pointer">
                    <input type="checkbox" checked={deliverables.includes(t)} onChange={() => toggleDeliverable(t)} className="accent-[#B0FC38]"
                      data-testid={`new-project-deliverable-${t}`} />
                    {t.replace(/_/g, ' ')}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
}
